import React, { useState } from "react";
import { View, StyleSheet, Platform, Alert } from "react-native";
import { Checkbox } from "expo-checkbox";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { useCSVDownload, CSVDownloadOptions, ExportScope } from "@/hooks/useCSVDownload";
import Animated, { useSharedValue, useAnimatedScrollHandler, useAnimatedRef } from "react-native-reanimated";
import VerticalScrollbar from "@/components/VerticalScrollBar";

export interface CSVDownloadModalProps extends CSVDownloadOptions {
  onClose: () => void;
}

export function CSVDownloadModal({
  data,
  filteredData,
  searchedData,
  currentPageData,
  selectedData,
  allColumns,
  headerFunc,
  override,
  baseFilename,
  onClose,
}: CSVDownloadModalProps) {
  const { colors } = useTheme();
  const [contentSize, setContentSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const scrollY = useSharedValue(0);
  const scrollViewRef = useAnimatedRef<Animated.ScrollView>();

  const { selectedColumns, scope, isGenerating, summary, toggleColumn, toggleAllColumns, setScope, downloadCSVFile } =
    useCSVDownload({
      data,
      filteredData,
      searchedData,
      currentPageData,
      selectedData,
      allColumns,
      headerFunc,
      override,
      baseFilename,
    });

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (event) => {
      scrollY.value = event.contentOffset.y;
    },
  });

  const handleContentSizeChange = (width: number, height: number) => {
    setContentSize({ width, height });
  };

  const handleScrollbarScroll = (scrollY: number) => {
    scrollViewRef.current?.scrollTo({ y: scrollY, animated: false });
  };

  const transformHeader = (key: string): string => {
    if (override && key in override) {
      return override[key];
    }
    return headerFunc?.(key) ?? key;
  };

  const handleDownload = async () => {
    try {
      await downloadCSVFile();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to generate CSV";
      if (Platform.OS === "web") {
        alert(message);
      } else {
        Alert.alert("Download Error", message);
      }
    }
  };

  const scopeOptions: { value: ExportScope; label: string; count: number; hidden?: boolean }[] = [
    {
      value: "filtered",
      label: "Filtered",
      count: summary.filteredCount,
      hidden: summary.filteredCount === summary.allCount,
    },
    {
      value: "searched",
      label: "Filtered + Search",
      count: summary.searchedCount,
      hidden: summary.searchedCount === summary.filteredCount,
    },
    { value: "page", label: "Current Page", count: summary.pageCount },
    { value: "all", label: "Whole Table", count: summary.allCount },
    { value: "selected", label: "Selected Rows", count: summary.selectedCount, hidden: summary.selectedCount === 0 },
  ];

  return (
    <View style={[styles.container]}>
      <ThemedText type="title" style={[styles.title, { color: colors.text }]}>
        Download CSV
      </ThemedText>

      {/* Column Selection */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>
            Select Columns ({summary.selectedColumnsCount}/{summary.totalColumnsCount})
          </ThemedText>
          <Button
            text={summary.selectedColumnsCount === summary.totalColumnsCount ? "Deselect All" : "Select All"}
            onPress={toggleAllColumns}
            style={[styles.toggleButton, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}
            textStyle={{ fontSize: 12 }}
          />
        </View>

        <View style={styles.scrollContainer}>
          <Animated.ScrollView
            ref={scrollViewRef}
            onScroll={scrollHandler}
            scrollEventThrottle={16}
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
            onContentSizeChange={handleContentSizeChange}
          >
            {allColumns.map((column) => (
              <View key={column} style={styles.checkboxRow}>
                {Platform.OS === "web" ? (
                  <input
                    type="checkbox"
                    checked={selectedColumns.has(column)}
                    onChange={() => toggleColumn(column)}
                    style={{ marginRight: 8 }}
                  />
                ) : (
                  <Checkbox
                    style={styles.checkbox}
                    value={selectedColumns.has(column)}
                    onValueChange={() => toggleColumn(column)}
                  />
                )}
                <ThemedText style={[styles.columnLabel, { color: colors.text }]}>{transformHeader(column)}</ThemedText>
              </View>
            ))}
          </Animated.ScrollView>

          <VerticalScrollbar
            scrollY={scrollY}
            contentHeight={contentSize.height}
            containerHeight={200}
            onScroll={handleScrollbarScroll}
            style={styles.scrollbar}
            autoHide={false}
            width={8}
          />
        </View>
      </View>

      {/* Scope Selection */}
      <View style={styles.section}>
        <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>Export Scope</ThemedText>

        <View style={styles.radioGroup}>
          {scopeOptions
            .filter((opt) => !opt.hidden)
            .map((opt) => (
              <View key={opt.value} style={styles.radioRow}>
                {Platform.OS === "web" ? (
                  <input
                    type="radio"
                    name="scope"
                    checked={scope === opt.value}
                    onChange={() => setScope(opt.value)}
                    style={{ marginRight: 8 }}
                  />
                ) : (
                  <Checkbox
                    style={styles.checkbox}
                    value={scope === opt.value}
                    onValueChange={() => setScope(opt.value)}
                  />
                )}
                <ThemedText style={[styles.radioLabel, { color: colors.text }]}>
                  {opt.label} ({opt.count} {opt.count === 1 ? "row" : "rows"})
                </ThemedText>
              </View>
            ))}
        </View>
      </View>

      {/* Summary */}
      <View style={[styles.summary, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
        <ThemedText style={[styles.summaryText, { color: colors.text }]}>
          Exporting {summary.selectedColumnsCount} columns × {summary.rowsToExport}{" "}
          {summary.rowsToExport === 1 ? "row" : "rows"}
        </ThemedText>
      </View>

      {/* Actions */}
      <View style={styles.actions}>
        <Button
          text="Cancel"
          onPress={onClose}
          style={[styles.actionButton, { backgroundColor: "transparent", borderColor: colors.border }]}
          textStyle={{ color: colors.text }}
        />
        <Button
          text={isGenerating ? "Generating..." : "Download CSV"}
          onPress={handleDownload}
          enabled={!isGenerating && summary.selectedColumnsCount > 0}
          style={[styles.actionButton, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white" }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    maxWidth: 500,
    minWidth: 400,
    elevation: 8,
  },
  section: {
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: "600",
    textAlign: "center",
    marginBottom: 20,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "500",
  },
  toggleButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
  },
  scrollContainer: {
    flexDirection: "row",
    height: 200,
    position: "relative",
  },
  scrollView: {
    flex: 1,
    maxHeight: 200,
  },
  scrollContent: {
    paddingRight: 16,
  },
  scrollbar: {
    position: "absolute",
    right: 0,
    top: 0,
    height: 200,
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
  },
  checkbox: {
    marginRight: 8,
  },
  columnLabel: {
    fontSize: 14,
    flex: 1,
  },
  radioGroup: {
    gap: 8,
  },
  radioRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
  },
  radioLabel: {
    fontSize: 14,
    flex: 1,
  },
  summary: {
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    marginBottom: 20,
  },
  summaryText: {
    fontSize: 13,
    textAlign: "center",
  },
  actions: {
    flexDirection: "row",
    gap: 12,
  },
  actionButton: {
    flex: 1,
    minHeight: 44,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 6,
    borderWidth: 1,
  },
});
